import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import PageGridItem from "./components/PageGridItem";
import { Banner } from "@/components/branding/Banner";
import { useGeotabClient } from "@/components/GeotabClientContext";
import PageHeader from "@/components/layout/PageHeader";
import { LicensedBoundary } from "@/components/LicensedBoundary";
import { groupPages, PageLink } from "@/constants/groupConfig";
import {
  NAVIGATE_ADDIN_EVENT,
  type NavigateAddInDetail,
} from "@/utilities/navigation/inStructureNavigation";

export default function HomePage() {
  const [currentPage, setCurrentPage] = useState<PageLink | null>(null);
  const { me } = useGeotabClient();
  const pages = groupPages();

  /**
   * Shows another page when one of them asks for it.
   *
   * Each page of this add-in is its own entry point, so a page cannot navigate anywhere by
   * itself — the launcher is the only thing that knows how to swap one for another. The map's
   * jump to the motor data of a vehicle goes through here.
   *
   * The page list is read inside the handler rather than closed over, so the listener is attached
   * once instead of on every render.
   */
  useEffect(() => {
    const handleNavigate = (event: Event) => {
      const requested = (event as CustomEvent<NavigateAddInDetail>).detail?.addInUrl;
      const target = groupPages().find(page => page.addInUrl === requested);
      if (target) {
        setCurrentPage(target);
      }
    };

    window.addEventListener(NAVIGATE_ADDIN_EVENT, handleNavigate);
    return () => window.removeEventListener(NAVIGATE_ADDIN_EVENT, handleNavigate);
  }, []);
  // -1 for admin page as it doesnt give isLoading
  const [loadingPages, setLoadingPages] = useState(pages.length - 1);
  if (currentPage) {
    return currentPage.name === "Admin" ? (
      <PageHeader
        handleHomeClick={() => setCurrentPage(null)}
        title={currentPage.name}
        content={currentPage.component}
        docs={currentPage.docs}
      />
    ) : (
      <LicensedBoundary name={currentPage.groupName}>
        <PageHeader
          handleHomeClick={() => setCurrentPage(null)}
          title={currentPage.name}
          content={currentPage.component}
          docs={currentPage.docs}
        />
      </LicensedBoundary>
    );
  }
  return (
    <PageHeader
      title={"Home"}
      content={
        <Stack
          sx={{
            width: "80%",
            mx: "auto",
            mt: "5%",
          }}
        >
          <Banner
            style={{
              width: "20%",
              minWidth: "200px",
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: "5%",
              display: "flex",
            }}
          />
          <Grid container spacing={5}>
            {pages.map(page => (
              <PageGridItem
                key={page.name}
                me={me}
                page={page}
                setCurrentPage={setCurrentPage}
                finishedLoading={() => setLoadingPages(prev => prev - 1)}
              />
            ))}
          </Grid>
          {loadingPages > 0 && <CircularProgress sx={{ mx: "auto", mt: "10%" }} />}
        </Stack>
      }
    />
  );
}
